import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Wrench, CheckCircle, Settings, Nut, Zap, Clock, Wifi, Shield, Truck, AlertTriangle } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Onsite from '../../assets/Images/services/onsite.jpg'

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};

const OnsiteHydraulicServices = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const controls = useAnimation();
  const [inViewRef, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('show');
    }
  }, [controls, inView]);

  const keyPoints = [
    'Mobile service team for excavators, crushers, and loaders',
    'Equipped for on-site diagnostics, hose replacement, and leak repair',
    'Immediate fault detection and hydraulic testing at client sites',
    '24/7 emergency breakdown assistance available',
    'Technicians trained in safety and preventive maintenance'
  ];

  const processSteps = [
    {
      icon: <AlertTriangle className="text-blue-600" size={24} />,
      title: 'Client Call & Diagnosis',
      description: 'Our team receives service requests and identifies probable issues.'
    },
    {
      icon: <Truck className="text-blue-600" size={24} />,
      title: 'Dispatch & Setup',
      description: 'Technicians arrive on-site with portable diagnostic tools.'
    },
    {
      icon: <Wrench className="text-blue-600" size={24} />,
      title: 'Inspection & Repair',
      description: 'Pressure readings and leakage points identified and repaired.'
    },
    {
      icon: <Nut className="text-blue-600" size={24} />,
      title: 'Replacement',
      description: 'Hoses, seals, or minor components replaced using genuine spares.'
    },
    {
      icon: <CheckCircle className="text-blue-600" size={24} />,
      title: 'Testing',
      description: 'System re-pressurized to confirm performance and eliminate leaks.'
    },
    {
      icon: <Shield className="text-blue-600" size={24} />,
      title: 'Preventive Action Report',
      description: 'Technician provides maintenance recommendations.'
    }
  ];

  const objectives = [
    'Minimize equipment downtime through rapid on-site intervention',
    'Reduce logistics costs associated with off-site repairs',
    'Provide consistent field support and emergency service coverage',
    'Increase equipment life with preventive maintenance guidance',
    'Ensure customer satisfaction through reliable mobile service'
  ];

  return (
    <div className="text-gray-800 leading-relaxed max-w-full overflow-x-hidden pt-20 md:pt-24">
      <Header />
      
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative bg-gray-50 py-16 md:py-24 overflow-hidden"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            {/* Left side - Text content */}
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              {/* <div className="flex items-center mb-4">
                <Wifi className="text-blue-600 mr-2" size={24} />
                <span className="text-blue-600 font-medium">Mobile Service Available</span>
              </div> */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00325b] mb-4 sm:mb-6">On-site Hydraulic Services</h1>
              <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8">
                Rapid response hydraulic maintenance and repair services for heavy equipment, minimizing downtime with our mobile service teams.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-300"
                >
                  Get a Free Quote
                </button>
                <button 
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="flex items-center justify-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium py-3 px-6 rounded-lg transition-colors duration-300"
                >
                  <Clock size={20} />
                  Emergency Service
                </button>
              </div>
            </div>
            
            {/* Right side - Image */}
            <div className="md:w-1/2 relative">
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src={Onsite} 
                  alt="On-site Hydraulic Services"
                  className="w-full h-auto rounded-xl transform hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent rounded-xl"></div>
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 bg-blue-100 rounded-full -z-10"></div>
              <div className="hidden md:block absolute -top-6 -right-6 w-24 h-24 bg-yellow-100 rounded-full -z-10"></div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Description Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1, transition: { duration: 0.6 } }}
        viewport={{ once: true }}
        className="relative py-20 bg-gradient-to-br from-blue-50 to-gray-50 overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 -left-20 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-20 -right-20 w-64 h-64 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center mb-16">
            {/* <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Our Mobile Service
            </span> */}
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Expert <span className="text-blue-600">On-Site Hydraulic Solutions</span>
            </h2>
            <div className="w-24 h-1.5 bg-blue-600 mx-auto mb-8"></div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              We provide comprehensive on-site hydraulic maintenance and repair services for heavy equipment, minimizing downtime with our mobile service teams.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto px-4">
            {/* Why Choose Us - Left Side */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 h-full">
              <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Zap className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Why Choose Us</h3>
              <ul className="space-y-4">
                {keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start group">
                    <div className="bg-green-100 p-1.5 rounded-full mr-3 mt-0.5 group-hover:bg-green-200 transition-colors">
                      <CheckCircle className="text-green-600" size={18} />
                    </div>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Our Objectives - Right Side */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 h-full">
              <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Settings className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Our Objectives</h3>
              <ul className="space-y-4">
                {objectives.map((objective, index) => (
                  <li key={index} className="flex items-start group">
                    <div className="bg-green-100 p-1.5 rounded-full mr-3 mt-0.5 group-hover:bg-green-200 transition-colors">
                      <CheckCircle className="text-green-600" size={18} />
                    </div>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      {/* <motion.section 
        ref={inViewRef}
        initial="hidden"
        animate={controls}
        variants={container}
        className="py-16 bg-white"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12 px-4">
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl font-bold text-gray-900 mb-4"
            >
              Our Service Process
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            >
              We follow a streamlined process to ensure your equipment is back in operation with minimal downtime.
            </motion.p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
            {processSteps.map((step, index) => (
              <motion.div 
                key={index} 
                variants={item}
                className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  {step.icon}
                </div>
                <motion.h3 
                  variants={fadeInUp}
                  className="text-xl font-semibold mb-2 text-gray-900"
                >
                  {step.title}
                </motion.h3>
                <motion.p 
                  variants={fadeInUp}
                  className="text-gray-600"
                >
                  {step.description}
                </motion.p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section> */}

      {/* Emergency Service Banner */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.6, ease: 'easeOut' }
        }}
        viewport={{ once: true }}
        className="bg-yellow-50 border-t border-b border-yellow-200 py-8"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <div className="bg-yellow-100 p-3 rounded-full mr-4">
                <AlertTriangle className="text-yellow-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">24/7 Emergency Service</h3>
                <p className="text-yellow-800">Available round the clock for urgent hydraulic repairs</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-300 whitespace-nowrap"
            >
              Call for Emergency Service
            </button>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.6, ease: 'easeOut' }
        }}
        viewport={{ once: true, amount: 0.2 }}
        id="contact" 
        className="bg-blue-700 text-white py-16"
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl font-bold mb-4"
          >
            Need On-Site Hydraulic Service?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-xl mb-8 max-w-2xl mx-auto"
          >
            Our mobile team is ready to assist you with all your hydraulic repair and maintenance needs.
          </motion.p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-700 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-300"
            >
              Schedule Service
            </button>
            {/* <a 
              href="tel:+1234567890"
              className="bg-transparent border-2 border-white hover:bg-white/10 font-medium py-3 px-8 rounded-lg transition-colors duration-300"
            >
              Call Now: (123) 456-7890
            </a> */}
          </div>
        </div>
      </motion.section>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1, transition: { delay: 0.3 } }}
        viewport={{ once: true }}
      >
        <Footer />
      </motion.div>
    </div>
  );
};

export default OnsiteHydraulicServices;
